import { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import Heading from '../../components/Text/Heading';
import { DocumentTextIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Container } from './styles';
import Input from '../../components/Form/Input';
import { ConditionData } from '../../types/Condition.types';
import { conditionService } from '../../api/services';
import { formatDate } from '../../components/DataTable/utils';
import ConditionActions from './ConditionActions';
import Button from '../../components/Form/Button';
import { useNavigate } from 'react-router-dom';
import PaginatedDataTable, { usePagination, useSorting } from '../../components/PaginatedDataTable';
import { getBadge } from '../Documents/utils';
import { getBadgeType } from './utils';

const Conditions = () => {
  const [models, setConditions] = useState<ConditionData[]>([]);
  const [deletedItem, setDeletedItem] = useState<string>('');

  const { limit, onPaginationChange, skip, pagination } = usePagination(10);
  const { sorting, onSortingChange, field, order } = useSorting('dataCriacao', 'DESC');
  const isMounted = useRef(false);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [resetPagination, setPaginationTrigger] = useState(false); // Inverter valor para reiniciar a paginaçao
  const [search, setSearch] = useState<string>('');

  const handleSearch = useCallback((e: { target: { value: SetStateAction<string> } }) => {
    setSearch(e.target.value);
  }, []);

  const DateFormatter = useCallback(
    (info: CellContext<ConditionData, unknown>) => formatDate(info.getValue() as string),
    []
  );

  const ConditionRelationType = useCallback(
    (info: CellContext<ConditionData, unknown>) => (
      <p style={{ textAlign: 'center' }}>{getBadge(info.getValue() as string)}</p>
    ),
    []
  );

  const ConditionType = useCallback(
    (info: CellContext<ConditionData, unknown>) => <>{getBadgeType(info.getValue() as string)}</>,
    []
  );

  const actions = useCallback(
    (info: CellContext<ConditionData, unknown>) => (
      <ConditionActions
        setDeletedItem={setDeletedItem}
        id={info.row.getValue('idModeloCondicao')}
        name={info.row.getValue('titulo')}
        relationType={info.row.getValue('tipoRelacao')}
      />
    ),
    []
  );

  const columns = useMemo<ColumnDef<ConditionData>[]>(
    () => [
      {
        accessorKey: 'idModeloCondicao',
        header: 'Id',
        enableGlobalFilter: false,
      },
      {
        accessorKey: 'titulo',
        header: 'Nome da condição',
        enableGlobalFilter: true,
      },
      {
        accessorKey: 'tipoRelacao',
        header: 'Característica',
        cell: ConditionType,
        enableGlobalFilter: true,
      },
      {
        accessorKey: 'descricao',
        header: 'Descrição',
        cell: ConditionRelationType,
        enableGlobalFilter: true,
      },
      {
        accessorKey: 'dataCriacao',
        header: 'Data da criação',
        cell: DateFormatter,
        enableGlobalFilter: true,
      },
      {
        accessorKey: 'dataAlteracao',
        header: 'Última alteração',
        cell: DateFormatter,
        enableGlobalFilter: true,
      },
      {
        accessorKey: 'usuarioCriacao',
        header: 'Criado por',
        enableGlobalFilter: true,
      },
      {
        accessorKey: 'action',
        header: '',
        cell: actions,
        enableSorting: false,
        enableGlobalFilter: false,
      },
    ],
    []
  );

  useEffect(() => {
    fetchData();
  }, [pagination, sorting, deletedItem]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    if (search.length < 3 && search.length != 0) {
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      fetchData(true);
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  const fetchData = async (_resetPagination = false) => {
    setLoading(true);
    try {
      const result = await conditionService.listPaged({
        pagination: { skip: _resetPagination ? 0 : skip, limit },
        sort: { field, order },
        query: search,
      });

      if (result) {
        setConditions(result.data);
        setCount(result.count);
        if (_resetPagination) {
          setPaginationTrigger(!resetPagination); //Retorna para pagina 1 após a pesquisa
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pageCount = Math.ceil(count / limit);
  const navigate = useNavigate();
  const navigateToCreateCondition = useCallback(() => {
    navigate('/conditions/create');
  }, []);

  return (
    <Container>
      <div className="header">
        <Heading>Condições</Heading>
        <Button onClick={navigateToCreateCondition}>
          <DocumentTextIcon />
          Cadastrar nova condição
        </Button>
      </div>

      <div className="search-filter-bar">
        <Input
          name="search"
          icon={<MagnifyingGlassIcon />}
          placeholder="Procurar por nome da condição, descrição..."
          onChange={handleSearch}
        />
      </div>
      <PaginatedDataTable<ConditionData>
        columnVisibility={{ idModeloCondicao: false }}
        data={models}
        columns={columns}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        pagination={pagination}
        paginationResetTrigger={resetPagination}
        loading={loading}
        pageCount={pageCount}
        sorting={sorting}
      />
    </Container>
  );
};

export default Conditions;
